import {
  isCardRedirect,
  type MembershipCardRedirect,
} from '../domain/cardChange';
import {
  decodeMarker,
  encodeMarker,
  isActivation,
  isRedirectContext,
  isWelcome,
  type MembershipActivation,
  type MembershipRedirectContext,
  type MembershipWelcome,
} from '../domain/purchase';

/** Session-scoped key-value storage: `sessionStorage` in the app, a map in tests. */
export interface MembershipKeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Everything the markers need from the outside world. */
export interface MembershipMarkersDeps {
  storage: MembershipKeyValueStorage;
  /** Signed-in customer; markers of anyone else are ignored, and nothing is written without one. */
  getCustomerId: () => string | null;
  now: () => number;
}

/** Storage keys of the purchase and card change markers. */
export const MEMBERSHIP_MARKER_KEY = {
  redirect: 'membership:purchase:redirect',
  activation: 'membership:purchase:activation',
  welcome: 'membership:purchase:welcome',
  cardRedirect: 'membership:card:redirect',
} as const;

const attempt = <T>(run: () => T, fallback: T): T => {
  try {
    return run();
  } catch {
    return fallback;
  }
};

/**
 * Purchase and card change state that has to survive a navigation, a reload
 * or a bank redirect: the redirect contexts, the "activating" flag and the
 * welcome banner. Every marker carries the customer and the time; a storage that
 * throws (private mode, a WebView without storage) reads as "no marker".
 */
export class MembershipMarkers {
  constructor(private readonly deps: MembershipMarkersDeps) {}

  readRedirect(): MembershipRedirectContext | null {
    return this.read(MEMBERSHIP_MARKER_KEY.redirect, isRedirectContext);
  }

  saveRedirect(context: MembershipRedirectContext): void {
    this.write(MEMBERSHIP_MARKER_KEY.redirect, context);
  }

  clearRedirect(): void {
    this.remove(MEMBERSHIP_MARKER_KEY.redirect);
  }

  readActivation(): MembershipActivation | null {
    return this.read(MEMBERSHIP_MARKER_KEY.activation, isActivation);
  }

  saveActivation(activation: MembershipActivation): void {
    this.write(MEMBERSHIP_MARKER_KEY.activation, activation);
  }

  clearActivation(): void {
    this.remove(MEMBERSHIP_MARKER_KEY.activation);
  }

  /**
   * Reads the welcome banner and removes it, so it is shown once. A marker
   * of another customer or an expired one is removed too: it can never be shown.
   */
  takeWelcome(): MembershipWelcome | null {
    const welcome = this.read(MEMBERSHIP_MARKER_KEY.welcome, isWelcome);
    this.remove(MEMBERSHIP_MARKER_KEY.welcome);
    return welcome;
  }

  saveWelcome(welcome: MembershipWelcome): void {
    this.write(MEMBERSHIP_MARKER_KEY.welcome, welcome);
  }

  readCardRedirect(): MembershipCardRedirect | null {
    return this.read(MEMBERSHIP_MARKER_KEY.cardRedirect, isCardRedirect);
  }

  saveCardRedirect(context: MembershipCardRedirect): void {
    this.write(MEMBERSHIP_MARKER_KEY.cardRedirect, context);
  }

  clearCardRedirect(): void {
    this.remove(MEMBERSHIP_MARKER_KEY.cardRedirect);
  }

  /** Forgets every marker, whoever wrote it; called on logout. */
  clearAll(): void {
    for (const key of Object.values(MEMBERSHIP_MARKER_KEY)) {
      this.remove(key);
    }
  }

  private read<T>(
    key: string,
    isValue: (value: unknown) => value is T,
  ): T | null {
    const raw = attempt(() => this.deps.storage.getItem(key), null);
    const value = decodeMarker(raw, this.deps.getCustomerId(), this.deps.now());
    return isValue(value) ? value : null;
  }

  private write(key: string, value: unknown): void {
    const customerId = this.deps.getCustomerId();
    if (customerId === null) {
      return;
    }
    attempt(
      () =>
        this.deps.storage.setItem(
          key,
          encodeMarker(value, customerId, this.deps.now()),
        ),
      undefined,
    );
  }

  private remove(key: string): void {
    attempt(() => this.deps.storage.removeItem(key), undefined);
  }
}
